import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

/**
 * BootstrapProvider — Phase 71 (2026-06-25).
 *
 * Blocking fetch of /api/bootstrap/public BEFORE the first paint of
 * every route. One request, one source of truth for:
 *   • content_blocks (founder-edited copy overrides)
 *   • brand (logo + 3 colours)
 *   • legal (last-updated timestamps + titles)
 *
 * This replaces the old defaults-then-fetch pattern that caused the
 * flash of default text. Default text no longer exists at the loading
 * boundary — if a block is unset, the EditableText component's hardcoded
 * fallback is used, which is still only one value, never two.
 *
 * Shape returned by /api/bootstrap/public:
 *   {
 *     content_blocks: { "nav.dashboard": "Home", ... },
 *     brand: { logo_url, primary_color, secondary_color, accent_color, updated_at },
 *     legal: { updated: {privacy, terms, cookie, disclaimer}, titles: {...} }
 *   }
 */

const BootstrapContext = createContext(null);

const DEFAULT_BRAND = {
  logo_url: "/dhdh-logo.png",
  primary_color: "#6B4E8A",
  secondary_color: "#FDF8F5",
  accent_color: "#E07A5F",
  updated_at: null,
};

export const BootstrapProvider = ({ children, apiBase }) => {
  const [ready, setReady] = useState(false);
  const [state, setState] = useState({
    content_blocks: {},
    brand: DEFAULT_BRAND,
    legal: { updated: {}, titles: {} },
  });

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/bootstrap/public`);
      const next = res?.data || {};
      setState({
        content_blocks: next.content_blocks || {},
        brand: { ...DEFAULT_BRAND, ...(next.brand || {}) },
        legal: {
          updated: (next.legal && next.legal.updated) || {},
          titles: (next.legal && next.legal.titles) || {},
        },
      });
    } catch {
      // On failure fall back to defaults so the app still renders.
    } finally {
      setReady(true);
    }
  }, [apiBase]);

  useEffect(() => { load(); }, [load]);

  // Founder edits dispatch this event so the whole provider stays live
  // without a full-page refresh.
  useEffect(() => {
    const onContentBlockChange = (e) => {
      if (!e?.detail?.block_key) return;
      setState((prev) => ({
        ...prev,
        content_blocks: { ...prev.content_blocks, [e.detail.block_key]: e.detail.value },
      }));
    };
    const onBrandChange = (e) => {
      if (!e?.detail) return;
      setState((prev) => ({ ...prev, brand: { ...prev.brand, ...e.detail } }));
    };
    window.addEventListener("dhdh:content-block-changed", onContentBlockChange);
    window.addEventListener("dhdh:brand-changed", onBrandChange);
    return () => {
      window.removeEventListener("dhdh:content-block-changed", onContentBlockChange);
      window.removeEventListener("dhdh:brand-changed", onBrandChange);
    };
  }, []);

  if (!ready) {
    // Minimal blank cream screen — no text, no logo, no flash.
    return (
      <div
        data-testid="bootstrap-loading"
        style={{
          minHeight: "100vh",
          background: state.brand.secondary_color || "#FDF8F5",
        }}
      />
    );
  }

  return (
    <BootstrapContext.Provider value={{ ...state, refresh: load }}>
      {children}
    </BootstrapContext.Provider>
  );
};

export const useBootstrap = () => {
  const ctx = useContext(BootstrapContext);
  if (!ctx) {
    return {
      content_blocks: {},
      brand: DEFAULT_BRAND,
      legal: { updated: {}, titles: {} },
      refresh: () => {},
    };
  }
  return ctx;
};

/** Convenience: content block lookup with inline fallback. */
export const useBlock = (block_key, fallback = "") => {
  const { content_blocks } = useBootstrap();
  const v = content_blocks[block_key];
  return v && v.length > 0 ? v : fallback;
};

/** Convenience: brand accessor. */
export const useBrand = () => {
  const { brand } = useBootstrap();
  return brand;
};

/** Convenience: legal last-updated map. */
export const useLegalMeta = () => {
  const { legal } = useBootstrap();
  return legal;
};

/** Broadcast a brand update so every subscriber re-renders instantly. */
export const dispatchBrandChange = (partial) => {
  window.dispatchEvent(new CustomEvent("dhdh:brand-changed", { detail: partial || {} }));
};
